/* eslint-disable @typescript-eslint/member-ordering */
import {computed, makeObservable, observable} from "mobx";
import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {AbstractModel} from "./abstract-model";
import {dateToEsri, esriToDate} from "../utils/date-utils";

export interface BuildingProps {
    objectId?: number;
    guid?: string;
    id?: number;
    artId?: number;
    abundanz?: number;
    abundanzBemerkung?: string;
    bekaempfung?: number;
    bemerkung?: string;
    populationsentwicklung?: number;
    zaehleinheit?: number;
    dateOfObservation?: Date;
}

/**
 * Auftragnehmer
 */
@JsonObject()
export class Building extends AbstractModel {

    static readonly comparableAttributes: string[] = [
        "id", "artId", "abundanz", "abundanzBemerkung", "bekaempfung", "praesenz", "bemerkung", "populationsentwicklung",
        "zaehleinheit", "dateOfObservation"
    ];
    // workaround, because feature template doesn't seem to work for table features
    static readonly prototypeAttributes: string[] = [
        "OBJECTID", "ID", "art_de", "art_lat", "abundanz", "abundanz_bemerkung", "bekaempfung", "praesenz", "bemerkung",
        "pop_entw", "zaehleinheit", "dateofobs"
    ];
    // field mappings
    static readonly databaseFieldNames: Map<string, string> = new Map<string, string>([
        ["objectId", "OBJECTID"],
        ["artId", "art_de"],
        ["art_lat", "art_lat"],     // for csv export to work
        ["abundanz", "abundanz"],
        ["abundanzBemerkung", "abundanz_bemerkung"],
        ["bekaempfung", "bekaempfung"],
        ["praesenz", "praesenz"],
        ["bemerkung", "bemerkung"],
        ["populationsentwicklung", "pop_entw"],
        ["zaehleinheit", "zaehleinheit"],
        ["dateOfObservation", "dateofobs"]
    ]);

    static readonly headerNames: Map<string, string> = new Map<string, string>([
        ["artId", "Art"],
        ["art_lat", "Art Latein"],
        ["dateOfObservation", "Datum Beobachtung"],
        ["bekaempfung", "Bekämpfung"],
        ["praesenz", "Präsenz"],
        ["populationsentwicklung", "Populationsentwicklung"],
        ["abundanz", "Abundanz"],
        ["zaehleinheit", "Zähleinheit"],
        ["bemerkung", "Bemerkung"]
    ]);

    class = "Building";
    typeLabel = "Gebäude";

    static sortByArt(a: Building, b: Building) {
        // TODO GRK: domain mapping
        const nameA = a.artId; // ignore upper and lowercase
        const nameB = b.artId; // ignore upper and lowercase
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    }

    /**
     * Eindeutiger Identifikator
     */
    @JsonProperty({name: "OBJECTID"})
        guid: string;

    /**
     * Art ID
     */
    @JsonProperty({name: "art_de"})
        artId: number;

    /**
     * Abundanz
     */
    @JsonProperty({name: "abundanz"})
        abundanz: number;

    /**
     * Abundanz Bemerkung
     */
    @JsonProperty({name: "abundanz_bemerkung"})
        abundanzBemerkung: string;

    /**
     * Bekaempfungsart
     */
    @JsonProperty({name: "bekaempfung"})
        bekaempfung: number;

    /**
     * Praesenz
     */
    @JsonProperty({name: "praesenz"})
        praesenz: number;

    /**
     * Bemerkung
     */
    @JsonProperty({name: "bemerkung"})
        bemerkung: string;

    /**
     * Populationsentwicklung
     */
    @JsonProperty({name: "pop_entw"})
        populationsentwicklung: number;

    /**
     * Zaehleinheit
     */
    @JsonProperty({name: "zaehleinheit"})
        zaehleinheit: number;

    /**
     * Datum der Beobachtung
     */
    @JsonProperty({
        name: "dateofobs",
        beforeDeserialize: esriToDate,
        afterSerialize: dateToEsri
    })
        dateOfObservation: Date;

    constructor(props?: BuildingProps) {
        super();
        if (props) {
            Object.assign(this, props);
        }
        // make observable after setting properties to avoid 'strict' messages
        makeObservable(this, {
            objectId: observable, // do observe to reload when object is saved
            guid: observable,

            artId: observable,
            abundanz: observable,
            abundanzBemerkung: observable,
            bekaempfung: observable,
            bemerkung: observable,
            praesenz: observable,
            populationsentwicklung: observable,
            zaehleinheit: observable,
            dateOfObservation: observable,

            isAlterable: computed,

            isNewRecord: computed
        });
    }

    get comparableAttributes(): string[] {
        return Building.comparableAttributes;
    }

    /**
     * If contractor properties are allowed to change.
     * Depends on relations to projects.
     */
    get isAlterable(): boolean {
        // TODO: Wann ist Editierbarkeit eingeschränkt? Was darf wann noch editiert werden?
        return true;
    }

}
